"use client";

import {
  CheckCircle2Icon,
  InfoIcon,
  Loader2Icon,
  OctagonAlertIcon,
  TriangleAlertIcon,
} from "lucide-react";
import type * as React from "react";
import { Toaster as Sonner } from "sonner";

import { tonalSurfaceVariants } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

type ToasterProps = Omit<React.ComponentProps<typeof Sonner>, "richColors">;

function Toaster({ className, ...props }: ToasterProps) {
  const baseToast = [
    "grid grid-cols-[auto_1fr_auto_auto_auto] items-center justify-items-start gap-2",
    "min-w-[240px] max-w-[480px] px-3 py-2",
    "rounded-md border border-border shadow-lg",
    "!text-left",
  ].join(" ");
  const baseButtons = "h-7 rounded-sm px-2 text-xs";
  const neutralToast = [baseToast, "bg-background text-foreground"].join(" ");

  const baseElementClasses = {
    icon: "col-start-1 row-start-1 grid size-4 place-items-center",
    content: "col-start-2 row-start-1 justify-self-start !text-left",
    title: "text-sm font-medium !text-left",
    description: "text-xs text-muted-foreground !text-left",
    loader:
      "!static !top-auto !left-auto !translate-x-0 !translate-y-0 col-start-1 row-start-1 size-4 place-self-center",
    closeButton:
      "col-start-5 row-start-1 grid size-5 justify-self-end place-items-center rounded-sm bg-transparent p-0 opacity-70 hover:opacity-100",
  } as const;

  const classNames = {
    toast: neutralToast,
    default: neutralToast,
    loading: neutralToast,
    success: tonalSurfaceVariants({ tone: "success" }),
    error: tonalSurfaceVariants({ tone: "danger" }),
    warning: tonalSurfaceVariants({ tone: "warning" }),
    info: tonalSurfaceVariants({ tone: "info" }),
    ...baseElementClasses,
    actionButton: `${baseButtons} col-start-3 row-start-1 ml-2 justify-self-end bg-background text-foreground`,
    cancelButton: `${baseButtons} col-start-4 row-start-1 justify-self-end bg-background text-foreground/80`,
  };

  const toastOptions = {
    ...props.toastOptions,
    unstyled: true,
    classNames,
    style: {
      textAlign: "left",
      direction: "ltr",
      ...(props.toastOptions?.style ?? {}),
    },
  } as const;

  return (
    <Sonner
      className={cn("toaster group", className)}
      toastOptions={toastOptions}
      richColors={false}
      icons={{
        success: <CheckCircle2Icon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonAlertIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      expand={props.expand ?? true}
      duration={props.duration ?? 3500}
      visibleToasts={props.visibleToasts ?? 4}
      gap={props.gap ?? 12}
      {...props}
    />
  );
}

export { toast } from "sonner";
export { Toaster };
